// The staff guide's content: the "How do I…" page and the guided tour both
// read these topics, so the words on the page and the words in the tour can
// never disagree.
//
// Every step was written from the code, not from memory (2026-10-30): button
// names are the labels the controls actually render. When a label changes,
// change it here too; guideTopics.test.js fails if a step names a target that
// no component carries.
//
// A step:
//   text     what to do, in the control's own words. Required.
//   target   a data-guide key (or several; the first one on screen wins).
//            The tour rings that control. Optional: a step with no target is
//            a plain explanation.
//   where    shown in the tour when the target is not on screen, so a step
//            never strands anyone ("Can't see it? …"). Required with target.
//   who      'teacher' | 'admin'. Absent = everyone. Grace is admin;
//            Annelise is a teacher (role employee). Admin-only controls are
//            written only into admin steps, so a teacher never reads about a
//            button she does not have.

export const GUIDE_GROUPS = [
	{ key: 'lesson', title: 'Lesson basics', icon: 'fa-chalkboard-user' },
	{ key: 'songs', title: 'Songs and tracks', icon: 'fa-music' },
	{ key: 'notes', title: 'Notes and recap', icon: 'fa-file-pen' },
	{ key: 'schedule', title: 'Schedule', icon: 'fa-calendar-week' },
];

// The first step of most topics: getting to Today. A teacher reaches it as a
// tab on her Dashboard; an admin through the sidebar.
const OPEN_TODAY = [
	{
		who: 'teacher',
		text: 'On your Dashboard, tap Today.',
		target: 'dash-tab-today',
		where: 'Tap the Color Your Voice logo at the top to get back to your Dashboard.',
	},
	{
		who: 'admin',
		text: 'Open Today in the sidebar.',
		target: 'nav-today',
		where: 'On a phone, open the menu at the top left first, then tap Today.',
	},
];

const OPEN_LESSON = {
	text: 'Open the lesson: on Today, tap Start beside the student. A lesson that has finished says Open instead.',
	target: 'today-start',
	where: 'Go to Today first. Start appears on lessons from today or earlier.',
};

const OPEN_SCHEDULE = [
	{
		who: 'teacher',
		text: 'On your Dashboard, tap Schedule.',
		target: 'dash-tab-schedule',
		where: 'Tap the Color Your Voice logo at the top to get back to your Dashboard.',
	},
	{
		who: 'admin',
		text: 'Open My Schedule in the sidebar, under Scheduling.',
		target: 'nav-my-schedule',
		where: 'On a phone, open the menu at the top left first, then tap My Schedule.',
	},
];

export const GUIDE_TOPICS = [
	{
		id: 'start-lesson',
		group: 'lesson',
		title: 'Start a lesson',
		summary: 'Open the Lesson Dashboard for a student on Today.',
		steps: [
			...OPEN_TODAY,
			{
				text: 'Choose My lessons to see only yours, or Full studio to see everyone teaching today.',
				target: 'today-scope',
				where: 'These two tabs sit just above the list of lessons on Today.',
			},
			{
				text: 'Tap Start beside the student. A lesson that has already finished says Open, so you can still add notes or a recap afterwards.',
				target: 'today-start',
				where: 'Start appears on lessons from today or earlier. Future lessons open on the day.',
			},
			{
				text: 'The Lesson Dashboard opens. The student’s songs, notes and recap are all on this one page, and the lesson timer starts from the top bar.',
			},
		],
	},
	{
		id: 'mark-attendance',
		group: 'lesson',
		title: 'Mark what happened',
		summary: 'Say whether the student came, did not show, or cancelled.',
		steps: [
			...OPEN_TODAY,
			{
				text: 'In the Status column, tap the lesson’s status (it has a small arrow).',
				target: 'today-outcome',
				where: 'You can mark a lesson once its day has come. Future lessons have nothing to mark yet.',
			},
			{
				who: 'teacher',
				text: 'Choose Attended, No-show or Cancelled · family.',
			},
			{
				who: 'admin',
				text: 'Choose Attended, No-show, Cancelled · family or Cancelled · CYV. Cancelled · CYV gives the family a makeup credit, and only admins see it.',
			},
			{
				text: 'Picked the wrong one? Tap the status again and choose the right one.',
			},
		],
	},
	{
		id: 'add-song',
		group: 'songs',
		title: 'Add a song to a student',
		summary: 'Songs are added inside a lesson, and land in the student’s repertoire.',
		steps: [
			OPEN_LESSON,
			{
				text: 'Tap Songs, above the notes.',
				target: ['lesson-songs', 'lesson-add-song'],
				where: 'Open the student’s lesson first. On a first lesson, tap Find a song instead.',
			},
			{
				text: 'At the bottom of the list, tap Add a song.',
				target: 'lesson-add-song',
				where: 'Tap Songs first; Add a song is the last line of its list.',
			},
			{
				text: 'Type the song title and press Enter (or Search on a phone keyboard).',
				target: 'addsong-search',
				where: 'Tap Add a song first.',
			},
			{
				text: 'Find the right version. The line under each title names the artist and album, and the play button plays a 30-second preview. Then tap Add.',
				target: 'addsong-add',
				where: 'Results appear after you search. Adding the show name helps for musicals.',
			},
			{
				text: 'Not in the list? Tap the button under the results that says Add it manually.',
				target: 'addsong-manual',
				where: 'It sits at the bottom of the Add a song sheet.',
			},
			{
				text: 'The song gets its own tab and is saved to the student’s repertoire. A new song then asks for a lyric video; you can find one now or later.',
			},
		],
	},
	{
		id: 'add-track',
		group: 'songs',
		title: 'Add a track',
		summary: 'Put a backing track on a song, from a file or a YouTube link.',
		steps: [
			OPEN_LESSON,
			{
				text: 'Tap the song’s tab so it is the song on screen.',
				target: 'lesson-song-tabs',
				where: 'Open the student’s lesson first. If the song is not there yet, add it (see Add a song).',
			},
			{
				text: 'Tap Add a track.',
				target: ['addtrack-open', 'strip-add-track'],
				where: 'On a wide screen it is in the Song panel on the right, under Tracks. On an iPad or phone, it is on the track strip near the top.',
			},
			{
				text: 'Choose where the music comes from: Choose a file (an mp3 or other audio file), one of the song’s saved videos, or paste a YouTube link.',
				target: ['addtrack-file', 'addtrack-link'],
				where: 'Tap Add a track first.',
			},
			{
				text: 'Tap Next.',
				target: 'addtrack-next',
				where: 'Next lights up once a file or link is chosen.',
			},
			{
				text: 'Remove vocals starts ticked. Untick it when the music is already a karaoke or instrumental version.',
				target: 'addtrack-vocals',
				where: 'This is on the second step, after Next.',
			},
			{
				text: 'Tap Add track. A line says which studio computer (or the cloud) is making it, and the track appears under Tracks when it is ready. You can keep teaching meanwhile.',
				target: 'addtrack-submit',
				where: 'This is on the second step, after Next.',
			},
			{
				text: 'Shortcut: when the song already has a lyric video saved, the Tracks row offers Make a track, which fills all of this in for you.',
				target: 'songpanel-tracks-action',
				where: 'Make a track shows only when a lyric video is saved and the song has no track yet.',
			},
		],
	},
	{
		id: 'jot-note',
		group: 'notes',
		title: 'Jot a song note',
		summary: 'Write a quick note on a song while you teach.',
		steps: [
			OPEN_LESSON,
			{
				text: 'Tap the song’s tab, or Warm-ups for anything general.',
				target: 'lesson-song-tabs',
				where: 'Open the student’s lesson first.',
			},
			{
				text: 'Type in the box at the bottom and press Enter, or tap the plus.',
				target: 'lesson-jot',
				where: 'The note box sits at the bottom of the lesson.',
			},
			{
				text: 'The note is yours until you deliver it to the student; it carries forward to the next lesson.',
			},
		],
	},
	{
		id: 'recap',
		group: 'notes',
		title: 'Write the recap and finish',
		summary: 'Send the family the lesson recap and close the lesson.',
		steps: [
			OPEN_LESSON,
			{
				text: 'Tap Recap at the top of the lesson.',
				target: 'lesson-recap',
				where: 'Open the student’s lesson first. Recap is in the top bar at every screen size.',
			},
			{
				text: 'Write in Everyone (the student and parents see it) and Parents (parents only). It saves as you type.',
			},
			{
				text: 'Tap Complete lesson at the bottom of the recap.',
				target: 'recap-complete',
				where: 'Tap Recap first; Complete lesson is at its bottom.',
			},
			{
				text: 'Email this recap starts ticked, so the family gets it. Untick it to skip the email.',
				target: 'wrapup-email',
				where: 'This shows after Complete lesson, when the family has an email on file.',
			},
			{
				text: 'Tap Complete lesson again. That stops the timer and closes the lesson.',
				target: 'wrapup-complete',
				where: 'Tap Complete lesson in the recap first.',
			},
		],
	},
	{
		id: 'my-schedule',
		group: 'schedule',
		title: 'See your schedule',
		summary: 'Which days and locations you are teaching.',
		steps: [
			...OPEN_SCHEDULE,
			{
				text: 'Your teaching days are listed by date and location, with the lessons you have taught.',
			},
		],
	},
	{
		id: 'coverage',
		group: 'schedule',
		title: 'Ask for a day to be covered',
		summary: 'Let Anthony or Grace know you need someone to cover a day.',
		steps: [
			...OPEN_SCHEDULE,
			{
				text: 'Under Need a day covered?, tap Request coverage beside the day.',
				target: 'coverage',
				where: 'This list shows only when you have teaching days coming up.',
			},
			{
				text: 'Add a reason if you like, then tap Send request. The day then says it is waiting on Anthony or Grace, and Cancel takes the request back.',
				target: 'coverage',
				where: 'Tap Request coverage first.',
			},
			{
				who: 'admin',
				text: 'Requests from teachers show on Teacher Scheduling, where you choose who covers.',
				target: 'nav-teacher-scheduling',
				where: 'On a phone, open the menu at the top left first.',
			},
		],
	},
];

/** The steps a viewer should see: everyone's steps, plus their role's. */
export const stepsFor = (topic, isAdmin) =>
	(topic?.steps || []).filter((s) => !s.who || s.who === (isAdmin ? 'admin' : 'teacher'));

export const topicById = (id) => GUIDE_TOPICS.find((t) => t.id === id) || null;

/** Every data-guide key the content names, for the drift test. */
export const allTargets = () => {
	const keys = new Set();
	GUIDE_TOPICS.forEach((t) =>
		t.steps.forEach((s) => [].concat(s.target || []).forEach((k) => keys.add(k)))
	);
	return [...keys];
};
